import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Hammer, Building2, Loader2,
    RotateCcw, Send, ChevronDown, CheckCircle,
    Navigation, IndianRupee,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { formatIndianCurrency, getAmountDisplay } from '../../utilities/amountToTextHelper';

import {
    fetchCCForWorkInProgress,
    fetchClientsByCCForWorkProgress,
    fetchSubClientsByClientForWP,
    fetchClientPOforWP,
    saveWorkProgress,
    clearClients,
    clearSubClients,
    clearPONumbers,
    clearSaveResult,
    resetWorkInProgress,
    selectCostCentersArray,
    selectCostCentersLoading,
    selectClientsArray,
    selectClientsLoading,
    selectSubClientsArray,
    selectSubClientsLoading,
    selectPONumbersArray,
    selectPONumbersLoading,
    selectSaveStatus,
    selectSaveLoading,
    selectSaveError,
} from '../../slices/accountsSlice/workInProgressSlice';

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-2xl">
        <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-indigo-500" />
            <div>
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
            </div>
        </div>
        {action}
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const WorkInProgress = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    // ── Redux ──────────────────────────────────────────────────────────────
    const costCenters = useSelector(selectCostCentersArray);
    const ccLoading = useSelector(selectCostCentersLoading);
    const clients = useSelector(selectClientsArray);
    const clientsLoading = useSelector(selectClientsLoading);
    const subClients = useSelector(selectSubClientsArray);
    const subClientsLoading = useSelector(selectSubClientsLoading);
    const poNumbers = useSelector(selectPONumbersArray);
    const poNumbersLoading = useSelector(selectPONumbersLoading);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    // ── Form state ────────────────────────────────────────────────────────
    const [costCenter, setCostCenter] = useState('');
    const [clientCode, setClientCode] = useState('');
    const [subClientCode, setSubClientCode] = useState('');
    const [poId, setPoId] = useState('');
    const [wpDate, setWpDate] = useState(null);
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [remarks, setRemarks] = useState('');

    // ── Init ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (userId) {
            dispatch(fetchCCForWorkInProgress({ userId, roleId }));
        }
        return () => {
            dispatch(resetWorkInProgress());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Fetch clients on CC change
    useEffect(() => {
        setClientCode(''); setSubClientCode(''); setPoId('');
        dispatch(clearClients()); dispatch(clearSubClients()); dispatch(clearPONumbers());
        if (costCenter) {
            dispatch(fetchClientsByCCForWorkProgress(costCenter));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costCenter]);

    // Fetch sub-clients on client change
    useEffect(() => {
        setSubClientCode(''); setPoId('');
        dispatch(clearSubClients()); dispatch(clearPONumbers());
        if (costCenter && clientCode) {
            dispatch(fetchSubClientsByClientForWP({ ccCode: costCenter, clientcode: clientCode }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientCode]);

    // Fetch PO numbers on sub-client change
    useEffect(() => {
        setPoId('');
        dispatch(clearPONumbers());
        if (costCenter && clientCode && subClientCode) {
            dispatch(fetchClientPOforWP({ ccCode: costCenter, client: clientCode, subClient: subClientCode }));
            setWpDate(new Date());
            setAmount('');
            setDescription('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subClientCode]);

    // Save result effect
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('Work Progress Details Added Successfully.');
            dispatch(clearSaveResult());
            handleFullReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleFullReset = () => {
        setCostCenter(''); setClientCode(''); setSubClientCode(''); setPoId('');
        setWpDate(null); setAmount(''); setDescription(''); setRemarks('');
        dispatch(resetWorkInProgress());
        if (userId) {
            dispatch(fetchCCForWorkInProgress({ userId, roleId }));
        }
    };

    const handleNumericChange = (setter) => (e) => {
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) setter(val);
    };

    const selectedPO = poNumbers.find((p) => String(p.ClientPOId) === String(poId));

    const handleSubmit = () => {
        if (!costCenter) { toast.warn('Select Cost Center.'); return; }
        if (!clientCode) { toast.warn('Select Client.'); return; }
        if (!subClientCode) { toast.warn('Select Sub Client.'); return; }
        if (!poId) { toast.warn('Select PO Number.'); return; }
        if (!wpDate) { toast.warn('Select Date.'); return; }
        if (!amount || parseFloat(amount) === 0) { toast.warn('Enter Amount.'); return; }
        if (!description.trim()) { toast.warn('Enter Description.'); return; }
        if (!remarks.trim()) { toast.warn('Enter Remarks.'); return; }

        dispatch(saveWorkProgress({
            ccCode: costCenter,
            clientcode: clientCode,
            subClientcode: subClientCode,
            poNumber: selectedPO?.PONumber || '',
            date: formatDateForAPI(wpDate),
            amount: parseFloat(amount) || 0,
            description,
            remarks,
            createdBy: userName,
            roleId,
        }));
    };

    // ── Derived ───────────────────────────────────────────────────────────
    const isBusy = saveLoading || ccLoading;
    const selectedCCName = costCenters.find((c) => c.CC_Code === costCenter)?.CC_Name || '—';
    const selectedClientName = clients.find((c) => c.ClientCode === clientCode)?.ClientName || '—';
    const selectedSubClientName = subClients.find((c) => c.SubClientCode === subClientCode)?.SubClientCodename || '—';

    const showSummary = costCenter && clientCode && subClientCode && poId &&
        wpDate && amount && parseFloat(amount) !== 0 && description.trim() && remarks.trim();

    const canSubmit = showSummary && !saveLoading;

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* ── Page Header ──────────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <Hammer className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Accounts Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'Work Progress'}
                                </h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Record work-in-progress amounts against a client PO</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">Accounts / Work Progress</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Section 1: Cost Center / Client / PO ─────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={Building2}
                        title="Cost Center, Client & PO"
                        subtitle="Select the cost center, client, sub-client and PO"
                        action={
                            <button
                                onClick={handleFullReset}
                                disabled={isBusy}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                            >
                                <RotateCcw className="h-3.5 w-3.5" /> Reset
                            </button>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div>
                                <Label required>Cost Center</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={costCenter}
                                        onChange={(e) => setCostCenter(e.target.value)}
                                        disabled={ccLoading || isBusy}
                                    >
                                        <option value="">{ccLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {costCenters.map((c) => (
                                            <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Code} — {c.CC_Name}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={ccLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Client</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={clientCode}
                                        onChange={(e) => setClientCode(e.target.value)}
                                        disabled={!costCenter || clientsLoading || isBusy}
                                    >
                                        <option value="">{!costCenter ? 'Select cost center first' : clientsLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {clients.map((c) => (
                                            <option key={c.ClientCode} value={c.ClientCode}>{c.ClientName}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={clientsLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Sub Client</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={subClientCode}
                                        onChange={(e) => setSubClientCode(e.target.value)}
                                        disabled={!clientCode || subClientsLoading || isBusy}
                                    >
                                        <option value="">{!clientCode ? 'Select client first' : subClientsLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {subClients.map((c) => (
                                            <option key={c.SubClientCode} value={c.SubClientCode}>{c.SubClientCodename}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={subClientsLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>PO Number</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={poId}
                                        onChange={(e) => setPoId(e.target.value)}
                                        disabled={!subClientCode || poNumbersLoading || isBusy}
                                    >
                                        <option value="">{!subClientCode ? 'Select sub client first' : poNumbersLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {poNumbers.map((p) => (
                                            <option key={p.ClientPOId} value={p.ClientPOId}>{p.PONumber}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={poNumbersLoading} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section 2: Work Progress Details ─────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={IndianRupee}
                        title="Work Progress Details"
                        subtitle="Date, amount, description and remarks"
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                            <div>
                                <Label required>Date</Label>
                                <CustomDatePicker
                                    value={wpDate}
                                    onChange={setWpDate}
                                    disabled={!poId || isBusy}
                                    maxDate={new Date()}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Label required>Amount (₹)</Label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    className={inputCls}
                                    value={amount}
                                    onChange={handleNumericChange(setAmount)}
                                    disabled={!poId || isBusy}
                                />
                                {parseFloat(amount) > 0 && (
                                    <p className="text-[10px] text-indigo-500 dark:text-indigo-400 mt-1 italic">
                                        {getAmountDisplay(parseFloat(amount)).words}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <Label required>Description</Label>
                                <textarea
                                    rows={3}
                                    placeholder="Enter description…"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    disabled={!poId || isBusy}
                                    className={`${inputCls} resize-none`}
                                />
                            </div>
                            <div>
                                <Label required>User Remarks</Label>
                                <textarea
                                    rows={3}
                                    placeholder="Enter remarks…"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    disabled={!poId || isBusy}
                                    className={`${inputCls} resize-none`}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Summary ───────────────────────────────────────────── */}
                {showSummary && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader icon={CheckCircle} title="Work Progress Summary" subtitle="Review before submitting" />
                        <div className="p-6 md:p-8">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                {[
                                    { label: 'Cost Center', value: `${costCenter} — ${selectedCCName}` },
                                    { label: 'Client', value: selectedClientName },
                                    { label: 'Sub Client', value: selectedSubClientName },
                                    { label: 'PO Number', value: selectedPO?.PONumber },
                                    { label: 'Date', value: formatDateForAPI(wpDate) },
                                    { label: 'Amount', value: `₹ ${formatIndianCurrency(parseFloat(amount) || 0)}` },
                                    { label: 'Created By', value: userName },
                                    { label: 'Description', value: description, wide: true },
                                    { label: 'Remarks', value: remarks, wide: true },
                                ].map(({ label, value, wide }) => (
                                    <div key={label} className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 ${wide ? 'md:col-span-3' : ''}`}>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                        <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value ?? '—'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Action Buttons ────────────────────────────────────── */}
                <div className="flex items-center justify-end gap-3 pb-6">
                    <button
                        onClick={handleFullReset}
                        disabled={isBusy}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                    >
                        <RotateCcw className="h-4 w-4" /> Reset
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {saveLoading
                            ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                            : <><Send className="h-4 w-4" /> Submit</>}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default WorkInProgress;
